<template>
  <UiModal
    v-model="shouldRootDynamicComponentShow"
    content-class="message-dialog"
    :forcing="true"
    :header-title="campaignTitle"
    :hide-overlay="false"
    :close-icon="true"
    @close-icon-event="handleModalClose"
  >
    <div class="wd-px-5 wd-pt-2 wd-pb-4 xs:(wd-py-4)">
      <v-img class="wd-rounded-4px" aspect-ratio="2.67" :src="getImage(campaignImage)" cover />
      <p v-if="campaignDescription" class="!wd-mb-0 wd-mt-2 wd-text-otempsm xs:(wd-mt-3 wd-text-omd)">
        {{ campaignDescription }}
      </p>
    </div>

    <div v-if="promoCodeList.length" class="wd-bg-gray20 wd-px-5 wd-py-6">
      <PromoCodeCard
        v-for="(promoCodeInfo, index) in promoCodeList"
        :key="promoCodeInfo.id"
        class="promoCodeCardContent"
        :is-in-modal="true"
        :is-active="true"
        :promo-code-info="promoCodeInfo"
        :is-button-loading="isCardLoadingList[index]"
        @button-click="claimPromoCode"
      />
    </div>
    <div v-else class="wd-min-h-75 wd-bg-gray20 wd-px-5 wd-flex wd-flex-col wd-items-center wd-justify-center">
      <div class="wd-mb-5 wd-py-3 wd-flex wd-justify-center">
        <v-img class="wd-w-30" src="@/assets/images/funnow/empty_promo_campaign.svg?url" cover />
      </div>
      <span class="wd-text-omd wd-text-gray80">{{ $t('campaign_null') }}</span>
    </div>

    <div
      v-if="shouldHintShow"
      class="text-white wd-absolute wd-bottom-6 wd-left-5 wd-right-5 wd-rounded-4px wd-flex wd-justify-between wd-items-center wd-pr-3 wd-pl-5 wd-py-4"
      :class="isHintSuccess ? 'wd-bg-green70' : 'wd-bg-red50'"
    >
      <span class="wd-text-omd wd-mr-3">{{ hintText }}</span>
      <v-btn width="40" height="40" icon variant="text" @click="hideHint">
        <funnow-icon class="wd-w-6 wd-h-6" max-width="24" height="24" image-path="cancel-icon_white.svg" />
      </v-btn>
    </div>
  </UiModal>
</template>

<script>
import utils from '@/utils'
import { getAssetsPath } from '@/utils/utils'
import * as memberService from '@/service/member'
import * as categoryService from '@/service/category'
import { useUserInfo } from '@/composition/useMember'
import { PromoCodeButtonStatus } from '@/view-models/member'

import UiModal from '@/components/dialog/ui-modal.vue'
import PromoCodeCard from '@/components/card/PromoCodeCard.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import { useGlobalStore } from '@/store-pinia/global'

export default defineComponent({
  name: 'PromoCampaignModal',
  components: {
    UiModal,
    PromoCodeCard,
    FunnowIcon,
  },
  setup() {
    const nuxtApp = useNuxtApp()
    const globalStore = useGlobalStore()
    const { shouldRootDynamicComponentShow, rootDynamicComponentOption } = storeToRefs(globalStore)

    const i18n = useI18n()
    const store = nuxtApp.$store
    const route = useRoute()
    const router = useRouter()
    const { userInfo } = useUserInfo()

    const campaignTitle = ref('')
    const campaignImage = ref('')
    const campaignDescription = ref('')
    const promoCodeList = ref([])
    const isCardLoadingList = ref([])
    const shouldHintShow = ref(false)
    const isHintSuccess = ref(true)
    const hintText = ref('')

    const promoCodeFromQuery = computed(() => route.query.promo_code)

    onMounted(() => {
      getCampaign()
    })

    watch(userInfo, (newUserInfo, oldUserInfo) => {
      if (!oldUserInfo && newUserInfo) {
        getCampaignAndRedeem()
      }
    })

    watch(
      () => store.getters['dialog/loginDialog'],
      newValue => {
        if (!newValue && !userInfo.value) {
          removePromoCampaignQuery()
        }
      }
    )

    watch(shouldHintShow, newValue => {
      if (newValue) {
        setTimeout(() => {
          hideHint()
        }, 5000)
      }
    })

    function hideHint() {
      shouldHintShow.value = false
    }

    function getImage(path) {
      return utils.imageUrl(path)
    }

    function openLoginDialog() {
      store.dispatch('dialog/setLoginDialog', { state: true })
    }

    function removePromoCampaignQuery() {
      const copiedQuery = { ...route.query }
      delete copiedQuery.promo_code
      delete copiedQuery.campaign_id
      router.replace({ query: copiedQuery }).catch(() => {})
    }

    // Note: 尚未實作領取 Fun Coins、會員身份 promo code 的處理
    async function verifyPromoCode(code) {
      const promoCodeIndex = promoCodeList.value.findIndex(promoCode => promoCode.code === code)
      isCardLoadingList.value = [...isCardLoadingList.value.slice(0, promoCodeIndex), true, ...isCardLoadingList.value.slice(promoCodeIndex + 1)]

      hideHint()

      try {
        if (promoCodeIndex < 0) {
          return
        }
        const res = await memberService.verifyPromoCode(nuxtApp.$axios, { promo_code: code })
        if (res.code !== 0) {
          return
        }

        isCardLoadingList.value = [...isCardLoadingList.value.slice(0, promoCodeIndex), false, ...isCardLoadingList.value.slice(promoCodeIndex + 1)]
        isHintSuccess.value = true
        shouldHintShow.value = true
        hintText.value = i18n.t('claimed_coupon_success', { coupon_title: res.data.promo_code.title })

        promoCodeList.value[promoCodeIndex].status = PromoCodeButtonStatus.CLAIMED
      } catch (err) {
        getCampaign()

        isCardLoadingList.value = [...isCardLoadingList.value.slice(0, promoCodeIndex), false, ...isCardLoadingList.value.slice(promoCodeIndex + 1)]
        isHintSuccess.value = false
        shouldHintShow.value = true
        hintText.value = err.message
      }
    }

    async function getCampaign() {
      const promoCampaignId = rootDynamicComponentOption.value.promoCampaignId
      const isLogin = !!userInfo.value

      try {
        const { data: promoCampaignInfo } = await categoryService.getPromoCampaign(nuxtApp.$axios, promoCampaignId, isLogin)
        if (!promoCampaignInfo) {
          return
        }

        promoCodeList.value = promoCampaignInfo.promo_codes
        isCardLoadingList.value = Array.from({ length: promoCampaignInfo.promo_codes.length }, () => false)
        campaignTitle.value = promoCampaignInfo.title
        campaignImage.value = promoCampaignInfo.cover_image_url
        campaignDescription.value = promoCampaignInfo.description
      } catch (err) {
        console.log('err is: ', err)
      }
    }

    async function getCampaignAndRedeem() {
      await getCampaign()

      const code = promoCodeFromQuery.value
      if (!code) {
        return
      }

      removePromoCampaignQuery()
      verifyPromoCode(code)
    }

    function claimPromoCode(promoCode) {
      if (!userInfo.value) {
        const copiedQuery = { ...route.query, campaign_id: rootDynamicComponentOption.value.promoCampaignId, promo_code: promoCode }
        router.push({ query: copiedQuery })

        openLoginDialog()
        return
      }

      verifyPromoCode(promoCode)
    }

    function handleModalClose() {
      removePromoCampaignQuery()
    }

    return {
      shouldRootDynamicComponentShow,
      handleModalClose,
      getImage,
      campaignTitle,
      campaignImage,
      campaignDescription,
      promoCodeList,
      isCardLoadingList,
      claimPromoCode,
      getAssetsPath,
      shouldHintShow,
      isHintSuccess,
      hintText,
      hideHint,
    }
  },
})
</script>

<style lang="scss" scoped>
.promoCodeCardContent {
  margin-bottom: 8px;
  &:last-of-type {
    margin-bottom: 0;
  }
}
</style>
